import React, { useMemo } from "react";
import ReactPlayer from "react-player";
import Webcam from "react-webcam";
import { Loader2 } from "lucide-react";

interface VideoCanvasProps {
  videoError: boolean;
  maxDeviceHeight: number | undefined;
  withPermissions: boolean | undefined;
  isVertical: boolean;
  aspectRatio: number;
  showPreview: boolean;
  previewUrl: string | undefined;
  showVideoLoader: boolean;
  canvasRef: React.RefObject<any>;
  bgType: string;
  hideWebcam: boolean;
  camera: string | undefined;
  webcamRef: React.RefObject<any>;
  videoConstraints: MediaTrackConstraints | boolean;
  audioConstraints: MediaTrackConstraints | boolean;
  setVideoError: (val: boolean) => void;
  setWithPermissions: (val: boolean | undefined) => void;
  setMountingNewWebcam: (val: boolean) => void;
  setLoadingWebcam: (val: boolean) => void;
  refreshDevices: () => void;
  mountingNewWebcam: boolean;
}

export const VideoCanvas: React.FC<VideoCanvasProps> = ({
  videoError,
  maxDeviceHeight,
  withPermissions,
  isVertical,
  aspectRatio,
  showPreview,
  previewUrl,
  showVideoLoader,
  canvasRef,
  bgType,
  hideWebcam,
  camera,
  webcamRef,
  videoConstraints,
  audioConstraints,
  setVideoError,
  setWithPermissions,
  setMountingNewWebcam,
  setLoadingWebcam,
  refreshDevices,
  mountingNewWebcam,
}) => {
  const previewPlayer = useMemo(() => {
    return (
      <ReactPlayer
        url={previewUrl}
        width={isVertical ? "auto" : "100%"}
        height={isVertical ? "560px" : "auto"}
        className="h-[560px] w-[315px] rounded-lg"
        controls
        playsinline
      />
    );
  }, [previewUrl, isVertical]);

  return (
    <div className="w-full flex flex-col items-center relative">
      {videoError && (
        <h2 className="text-[#ff4136] mx-auto pt-[130px] px-5 pl-[30px] text-center h-[400px] w-[315px] text-lg font-bold">
          {`The selected camera is not supported at this time. Please select a different camera. (details: ${maxDeviceHeight})`}
        </h2>
      )}
      {withPermissions === false && (
        <h2 className="text-[#ff4136] mx-auto pt-[130px] px-5 pl-[30px] text-center h-[400px] w-[315px] text-lg font-bold">
          Please allow camera and microphone access to start recording
        </h2>
      )}
      <div
        className={
          "rounded-lg flex [&_video]:object-cover [&_video]:object-center [&_video]:overflow-auto relative " +
          (isVertical ? "w-[315px] h-[560px]" : "w-full")
        }
        style={{ aspectRatio }}
      >
        {showPreview && previewPlayer}
        {showVideoLoader && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 rounded-lg">
            <Loader2 className="size-6 text-zinc-400 animate-spin mb-2" />
            <span className="text-xs text-zinc-500 font-medium">Loading Camera Feed...</span>
          </div>
        )}
        {mountingNewWebcam ? undefined : (
          <>
            <canvas
              ref={canvasRef}
              className={bgType === "none" || hideWebcam ? "hidden" : "rounded-lg w-full h-full object-cover"}
            />
            <Webcam
              key={`webcam-${camera}-${isVertical}`}
              className={hideWebcam || bgType !== "none" ? "hidden" : "rounded-lg"}
              audio={true}
              muted={true}
              ref={webcamRef}
              disablePictureInPicture={true}
              mirrored={false}
              imageSmoothing={true}
              forceScreenshotSourceSize={true}
              screenshotFormat={"image/webp"}
              screenshotQuality={1}
              videoConstraints={videoConstraints}
              audioConstraints={audioConstraints}
              onUserMediaError={e => {
                console.error("Error loading user media", e);
                if (e.toString().includes("OverconstrainedError")) {
                  setVideoError(true);
                } else if (e.toString().includes("NotAllowedError")) {
                  setWithPermissions(false);
                }
                setMountingNewWebcam(false);
                setLoadingWebcam(false);
              }}
              onLoadStart={() => setLoadingWebcam(true)}
              onLoadedMetadata={() => setLoadingWebcam(false)}
              onUserMedia={() => {
                setWithPermissions(true);
                setMountingNewWebcam(false);
                refreshDevices();
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
